import { Link } from "react-router-dom";
import LeadMagnetPage from "@/components/LeadMagnetPage";

const GUIDES = {
  "industrial-curtain-buyers-guide": {
    guideName: "Industrial Curtain Buyers Guide",
    title: "The Australian Industrial Curtain Buyer's Guide",
    subtitle: "Everything you need to know before purchasing industrial curtains for your facility.",
    bullets: [
      "Compare curtain types: sliding, fixed, strip, acoustic, welding",
      "Material options explained: PVC, clear PVC, mesh",
      "Track and mounting systems — which one fits your setup",
      "Price ranges and what affects cost",
      "Installation process and what to expect",
      "Checklist: 10 questions to ask any supplier",
    ],
    contents: [
      "Why industrial curtains are replacing permanent walls",
      "Types of industrial curtains and when to use each",
      "Material options: PVC, clear PVC, mesh — pros and cons",
      "Track and mounting systems explained",
      "Key features and add-ons (vision panels, floor sweeps, entry points)",
      "How to measure your space",
      "What to look for in a supplier",
      "Price ranges and what affects cost",
      "Installation: what to expect",
      "Maintenance and care guide",
      "Checklist: questions to ask before buying",
    ],
    audience: "Operations Managers, Facility Managers, Procurement Managers, and anyone responsible for industrial facility improvements in Australia.",
  },
  "factory-partition-planning-guide": {
    guideName: "Factory Partition Planning Guide",
    title: "Factory Partition Planning Guide",
    subtitle: "How to divide your facility for maximum efficiency — without permanent construction.",
    bullets: [
      "Common partition layouts: straight, L-shape, U-shape, enclosure",
      "PVC vs brick vs steel — cost, speed, and flexibility compared",
      "Compliance considerations for your industry",
      "Step-by-step measurement checklist",
      "Real case study from an Australian factory",
    ],
    contents: [
      "Why partition your factory",
      "Common partitioning layouts and configurations",
      "Choosing between permanent and flexible partitions",
      "Material selection for factory environments",
      "Compliance considerations (fire, safety, food standards)",
      "How to plan your partition project (measurement checklist)",
      "Cost comparison: PVC vs brick vs steel",
      "Case study: successful factory partitioning",
      "Working with a fabrication specialist",
    ],
    audience: "Factory Managers, Operations Managers, Project Engineers, and Business Owners planning facility improvements.",
  },
  "dust-control-guide": {
    guideName: "Dust Control Solutions Guide",
    title: "The Industrial Dust Control Guide",
    subtitle: "Practical dust containment solutions for Australian factories and warehouses.",
    bullets: [
      "The real cost of uncontrolled dust (health, product, equipment)",
      "WHS regulations you need to know",
      "Containment vs suppression vs extraction — when to use each",
      "PVC wall solutions for permanent dust control",
      "ROI calculation template included",
    ],
    contents: [
      "The real cost of uncontrolled dust",
      "Health and safety implications (WHS regulations)",
      "Types of dust control: containment vs suppression vs extraction",
      "When PVC containment walls are the best solution",
      "Curtain vs wall solutions for dust control",
      "Material specifications for dust containment",
      "Case studies: dust control in warehousing, mining, manufacturing",
      "Maintenance of dust containment systems",
      "ROI calculation template",
    ],
    audience: "Safety Managers, WHS Officers, Operations Managers, and anyone dealing with dust problems in industrial facilities.",
  },
  "warehouse-optimisation-guide": {
    guideName: "Warehouse Optimisation Guide",
    title: "Warehouse Optimisation Guide",
    subtitle: "Maximise space, efficiency, and safety in your warehouse operation.",
    bullets: [
      "Signs your warehouse needs optimisation",
      "Zone planning for efficiency and safety",
      "Temperature and dust management strategies",
      "Flexible solutions that don't require construction",
      "Energy cost reduction through smart partitioning",
      "Optimisation assessment checklist included",
    ],
    contents: [
      "Signs your warehouse needs optimisation",
      "Zone planning: separating operations for efficiency",
      "Temperature zone management",
      "Traffic flow and access management",
      "Safety zone creation",
      "Flexible vs permanent solutions",
      "How PVC walls and curtains enable optimisation",
      "Energy cost reduction through partitioning",
      "Case study: warehouse transformation",
      "Optimisation assessment checklist",
    ],
    audience: "Warehouse Managers, Logistics Managers, Operations Directors, and 3PL operators looking to improve facility performance.",
  },
  "maintenance-checklist": {
    guideName: "Maintenance Checklist",
    title: "Industrial Curtain & PVC Wall Maintenance Checklist",
    subtitle: "Keep your industrial textile products in top condition with this simple maintenance guide.",
    bullets: [
      "Monthly inspection checklist (12 items)",
      "Quarterly maintenance tasks",
      "Signs your curtain needs repair or replacement",
      "Cleaning guide for industrial PVC",
      "Quick, printable format",
    ],
    contents: [
      "Monthly inspection checklist (12 items)",
      "Quarterly maintenance tasks",
      "Annual service recommendations",
      "Signs your curtain needs repair or replacement",
      "How to clean industrial PVC",
      "When to call for professional service",
    ],
    audience: "Maintenance teams, Facility Managers, and anyone responsible for maintaining industrial curtains, PVC walls, or textile containment systems.",
  },
};

export function GuidePage({ slug }) {
  const guide = GUIDES[slug];
  if (!guide) return <div className="min-h-screen bg-[#0A0A0A] pt-24 text-center text-[#A3A3A3]">Guide not found</div>;
  return <LeadMagnetPage {...guide} />;
}

export default function GuidesIndexPage() {
  return (
    <div data-testid="guides-page" className="min-h-screen bg-[#0A0A0A] pt-28 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="industrial-line mb-6" />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
            Free <span className="gradient-text">Guides & Resources</span>
          </h1>
          <p className="text-base text-[#A3A3A3] max-w-xl">
            Practical guides to help you make informed decisions about industrial textile solutions for your facility.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(GUIDES).map(([slug, g]) => (
            <Link
              key={slug}
              to={`/guides/${slug}`}
              className="bg-[#141414] border border-[#2E2E2E] p-6 hover:border-[#FF5722] transition-all duration-300 group"
            >
              <span className="text-[#FF5722] text-[10px] font-bold uppercase tracking-wider">Free Guide</span>
              <h3 className="text-base font-bold mt-2 mb-3 group-hover:text-[#FF5722] transition-colors" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                {g.title}
              </h3>
              <p className="text-xs text-[#A3A3A3] line-clamp-2">{g.subtitle}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
